import { useState, useCallback, useEffect, useRef } from 'react';
import {
  loadReminders,
  createReminder,
  updateReminder,
  dismissReminder,
} from '@/store/reminders/actions';
import type { UserId } from '@/shared/types/user';
import type { ReminderCreate, ReminderUpdate } from '@/store/reminders/types';
import { useApiWithAuth } from '@/hooks/api';
import { useRemindersStore } from '@/store/reminders';
import { useActiveReminders, useUpcomingReminders } from './computed';

export function useReminders(assignee: UserId, limit?: number) {
  const { api, auth } = useApiWithAuth();
  const store = useRemindersStore();
  const [loading, setLoading] = useState(false);
  const loaded = useRef(false);

  const loadUserReminders = useCallback(async () => {
    if (loaded.current || loading) {
      return;
    }

    setLoading(true);
    loaded.current = true;

    try {
      const response = await loadReminders({ api, auth, assignee, limit });
      store.dispatch({ type: 'setReminders', data: response });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [api, auth, assignee, loading, limit, store]);

  useEffect(() => {
    if (assignee) {
      loadUserReminders();
    }
  }, [assignee, loadUserReminders]);

  const active = useActiveReminders(store);
  const upcoming = useUpcomingReminders(store);

  return {
    loading,
    reminders: store.state.list,
    active,
    upcoming,
    loadReminders: loadUserReminders,
  };
}

export function useCreateReminder() {
  const { api, auth } = useApiWithAuth();
  const [creating, setCreating] = useState(false);
  const { dispatch } = useRemindersStore();

  const handleCreate = async (reminder: ReminderCreate) => {
    setCreating(true);
    try {
      const result = await createReminder({ api, auth, reminder });
      dispatch({ type: 'addReminder', data: result });

      return result;
    } finally {
      setCreating(false);
    }
  };

  return {
    createReminder: handleCreate,
    creating,
  };
}

export function useUpdateReminder() {
  const { api, auth } = useApiWithAuth();
  const [updating, setUpdating] = useState(false);
  const { dispatch } = useRemindersStore();

  const handleUpdate = async (reminder: ReminderUpdate) => {
    setUpdating(true);
    try {
      const result = await updateReminder({ api, auth, reminder });
      dispatch({ type: 'replaceReminder', data: result });

      return result;
    } finally {
      setUpdating(false);
    }
  };

  return {
    updateReminder: handleUpdate,
    updating,
  };
}

export function useDismissReminder() {
  const { api, auth } = useApiWithAuth();
  const [dismissing, setDismissing] = useState(false);
  const { dispatch } = useRemindersStore();

  const handleDismiss = async (id: string) => {
    setDismissing(true);
    try {
      const result = await dismissReminder({ api, auth, id });
      dispatch({ type: 'dismissReminder', id });

      return result;
    } finally {
      setDismissing(false);
    }
  };

  return {
    dismissReminder: handleDismiss,
    dismissing,
  };
}
